<script setup lang="ts">
  import { ref, onMounted, onUnmounted } from "vue";
  import { useI18n } from "vue-i18n";
  import { Modal, Text, Button, useNotification, IconBarcode } from "@ui-kit";
  import { useBarcode, useReadRfid } from "@plugins";
  import {
    type Product,
    ResponseCode,
    WTFIRInputType,
    useGetWTFIR,
    useLazyProductsLoad,
    useLazyItemLoadByGuid,
    itemSetZoneApi,
  } from "@src/api";
  import { chunkArray } from "@src/utils";
  import { default as AddProductItem } from "./AddProductItem.vue";
  import { default as ProductZoningExistZoneDialog } from "./ProductZoningExistZoneDialog.vue";

  const { subtitle, zone_id, zone_item_count } = defineProps<{
    zone_id: string;
    subtitle?: string;
    zone_item_count: number;
  }>();
  const emit = defineEmits<{
    "close": [value: void];
    "add:zone": [value: Product[]];
  }>();

  const barcode = useBarcode();
  const read_rfid = useReadRfid();
  const i18n = useI18n();
  const notification = useNotification();
  const { execute: getWTFIR } = useGetWTFIR();
  const { execute: getProduct } = useLazyProductsLoad();
  const { execute: getItemLoadByGuid } = useLazyItemLoadByGuid();

  const item_list = ref<Product[]>([]);
  const item_existing_in_zone = ref<{ product: Product; zoneIds: string[] } | null>(null);

  const handleAddProduct = async (data: string, type: WTFIRInputType) => {
    try {
      const { data: wtfirData } = await getWTFIR({ data, input_type: type });

      if (!wtfirData.value.items?.length) {
        throw { title: i18n.t("Товар не найден"), type: "error" };
      }

      const productResult = await getProduct({ item_id: wtfirData.value.items });
      const parentProductResult = await getItemLoadByGuid({ guid: productResult.data.value.items.at(0)?.seria_guid });

      if (productResult.isError.value || parentProductResult.isError.value) {
        throw { title: i18n.t("Ошибка получения товара"), type: "error" };
      }

      const existingProductInList = item_list.value.find(
        (item) => item.item_id === parentProductResult.data.value.item.item_id,
      );

      if (existingProductInList) {
        throw { title: i18n.t("Товар уже добавлен в список"), type: "warning" };
      }

      const productExistInZone = parentProductResult.data.value.zones.find((item) => item.zone_id === zone_id);

      if (productExistInZone) {
        throw { title: i18n.t("Товар уже в этой зоне"), type: "warning" };
      }

      if (parentProductResult.data.value.zones.length > 0) {
        item_existing_in_zone.value = {
          product: parentProductResult.data.value.item,
          zoneIds: parentProductResult.data.value.zones.map((item) => item.zone_id),
        };
      } else {
        item_list.value.unshift(parentProductResult.data.value.item);
      }
    } catch (e) {
      notification.show({ title: e.title, type: e.type });
    }
  };

  const handleSetZone = async () => {
    const response_all = await Promise.all(
      chunkArray(item_list.value, 10).map(async (chunk) => {
        try {
          const result = await itemSetZoneApi({ item_id: chunk.map((item) => item.item_id), zone_id });

          if (result.code === ResponseCode.OK) {
            emit("add:zone", chunk);

            return chunk.length;
          }

          return 0;
        } catch {
          notification.show({ title: i18n.t("Ошибка при добавлении товаров"), type: "error" });

          return 0;
        }
      }),
    );

    const added_count = response_all.reduce((sum, n) => sum + n, 0);

    if (added_count > 0) {
      notification.show({
        title: `${i18n.t("Привязано товаров")}: ${added_count}`,
        description: `${i18n.t("Всего товаров в зоне")}: ${zone_item_count}`,
        type: "success",
      });
    }

    emit("close");
  };

  onMounted(() => {
    barcode.subscribe((data) => {
      handleAddProduct(data, WTFIRInputType.barcode);
    });
    read_rfid.subscribe((data) => {
      handleAddProduct(data.value, WTFIRInputType.rfid);
    });
  });

  onUnmounted(() => {
    barcode.unsubscribe();
    read_rfid.unsubscribe();
  });
</script>

<template>
  <Modal close grabber isFullHeight @onClose="emit('close')" data-testid="add-product-modal">
    <template #header>
      <div class="add-product-modal__header" data-testid="add-product-modal-header">
        <Text font="body-bold-18" class="add-product-modal__header-title">{{ $t("Добавление товара") }}</Text>
        <Text font="body-regular-14" color="secondary">{{ subtitle }}</Text>
      </div>
    </template>
    <div v-if="item_list.length === 0" class="add-product-modal__empty" data-testid="add-product-modal-empty">
      <IconBarcode />
      <div class="add-product-modal__empty-text">
        <Text font="body-medium-16" color="secondary">{{ $t("Список товаров пуст") }}</Text>
        <Text font="body-regular-14" color="secondary">{{
          $t("Отсканируйте товар, который хотите{0}добавить", ["\n"])
        }}</Text>
      </div>
    </div>
    <div v-else class="add-product-modal__items">
      <AddProductItem
        v-for="product in item_list"
        :key="product.item_id"
        :image="product.image_url"
        :name="product.title"
        :article="product.article"
        :color="product.color"
        @click:delete="item_list = item_list.filter((item) => item.item_id !== product.item_id)"
        data-testid="add-product-modal-product-item"
      />
    </div>
    <template #footer v-if="item_list.length > 0">
      <Button class="add-product-modal__add-btn" @click="handleSetZone" data-testid="add-product-modal-add-btn">
        {{ $t("Сохранить") }}
      </Button>
    </template>
  </Modal>
  <ProductZoningExistZoneDialog
    :product="item_existing_in_zone?.product ?? null"
    :zoneIds="item_existing_in_zone?.zoneIds ?? null"
    @close="item_existing_in_zone = null"
    @click:attach="
      () => {
        item_list.unshift(item_existing_in_zone?.product);
        item_existing_in_zone = null;
      }
    "
  />
</template>

<style scoped>
  .add-product-modal__header {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .add-product-modal__header-title {
    padding: 3px 0px;
  }

  .add-product-modal__empty {
    display: flex;
    flex-direction: column;
    flex: 1;
    justify-content: center;
    align-items: center;
    gap: 12px;
    overflow: hidden;
  }

  .add-product-modal__empty-text {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    white-space: pre-wrap;
    text-align: center;
  }

  .add-product-modal__items {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .add-product-modal__add-btn {
    width: 100%;
  }
</style>
